// SYSTEM_ROUTE: /finance
import { useRef, useState } from 'react';
import type { Account, AccountType, FinanceSummary, ImportBatch, ImportRow, Transaction, TransactionKind, TransactionRail } from '@life-os/app-core';
import { ACCOUNT_TYPES, TRANSACTION_KINDS, formatMinor } from '@life-os/app-core';
import { Button, Chip, CompactRow, EmptyState, Pane, Stack } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

interface RawImportRow {
  occurredOn: string;
  occurredAt: string | null;
  direction: 'inflow' | 'outflow';
  amountMinor: number;
  counterparty: string | null;
  upiRef: string | null;
  bankRef: string | null;
  rail: string | null;
  narration: string;
  balanceAfterMinor: number | null;
}

function toImportRows(raw: RawImportRow[]): ImportRow[] {
  return raw.map((r) => ({
    occurredOn: r.occurredOn,
    occurredAt: r.occurredAt ?? null,
    txnKind: r.direction === 'inflow' ? 'income' : 'expense',
    amountMinor: r.amountMinor,
    counterparty: r.counterparty ?? null,
    externalReference: r.bankRef ?? r.upiRef ?? null,
    rail: (r.rail as TransactionRail | null) ?? null,
    balanceAfterMinor: r.balanceAfterMinor ?? null,
    narration: r.narration ?? '',
  }));
}

const inputStyle = {
  background: 'var(--lc-surface-container)',
  color: 'var(--lc-on-surface)',
  border: '1px solid var(--lc-outline-variant)',
  borderRadius: 'var(--lc-radius-full)',
  padding: '0 12px',
  height: 34,
  font: 'inherit',
  fontSize: 'var(--lc-text-sm)',
} as const;

interface AccountRow {
  account: Account;
  balanceMinor: number;
}

function monthRange(): { from: string; to: string } {
  const now = new Date();
  const from = `${now.toISOString().slice(0, 7)}-01`;
  const to = now.toISOString().slice(0, 10);
  return { from, to };
}

export function FinanceScreen() {
  const services = useServices();
  const { revision, refresh } = useStore();

  const [acctName, setAcctName] = useState('');
  const [acctType, setAcctType] = useState<AccountType>('bank');
  const [opening, setOpening] = useState('0');

  const [txnAccount, setTxnAccount] = useState('');
  const [txnKind, setTxnKind] = useState<TransactionKind>('expense');
  const [txnAmount, setTxnAmount] = useState('');
  const [txnCategory, setTxnCategory] = useState('');

  const [importAccount, setImportAccount] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const accounts = useAsync<AccountRow[]>(async () => {
    const list = await services.finance.listAccounts();
    return Promise.all(list.map(async (account) => ({ account, balanceMinor: await services.finance.balanceOf(account.id) })));
  }, [revision]);

  const netWorth = useAsync<number>(() => services.finance.netWorth(), [revision]);
  const { from, to } = monthRange();
  const summary = useAsync<FinanceSummary>(() => services.finance.summary(from, to), [revision]);
  const recent = useAsync<readonly Transaction[]>(() => services.finance.listTransactions(), [revision]);
  const batches = useAsync<readonly ImportBatch[]>(() => services.finance.listImportBatches(), [revision]);

  const rows = accounts.value ?? [];
  const currency = rows[0]?.account.currency ?? 'USD';

  const toMinor = (major: string): number => Math.round((Number(major) || 0) * 100);

  const addAccount = async () => {
    if (acctName.trim().length === 0) return;
    await services.finance.createAccount({ name: acctName, accountType: acctType, openingBalanceMinor: toMinor(opening) });
    setAcctName('');
    setOpening('0');
    refresh();
  };

  const addTxn = async () => {
    const accountId = txnAccount || rows[0]?.account.id;
    const amount = toMinor(txnAmount);
    if (!accountId || amount <= 0) return;
    await services.finance.recordTransaction({
      accountId: accountId as Account['id'],
      txnKind,
      amountMinor: amount,
      category: txnCategory || 'uncategorized',
    });
    setTxnAmount('');
    setTxnCategory('');
    refresh();
  };

  const s = summary.value;
  const byCategory = s ? Object.entries(s.byCategory).sort((a, b) => a[1] - b[1]) : [];

  const runImport = async (file: File) => {
    const accountId = importAccount || rows[0]?.account.id;
    if (!accountId) {
      setImportMsg('Add a bank account first, then import into it.');
      return;
    }
    setImporting(true);
    setImportMsg(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as RawImportRow[];
      if (!Array.isArray(parsed)) {
        setImportMsg('That file is not a normalized ledger (expected a JSON array).');
        return;
      }
      const result = await services.finance.importStatement({
        accountId: accountId as Account['id'],
        sourceLabel: file.name.replace(/\.json$/i, ''),
        sourceFiles: [file.name],
        rows: toImportRows(parsed),
      });
      setImportMsg(`Imported ${result.created} new transaction${result.created === 1 ? '' : 's'}; skipped ${result.skipped} already present.`);
      refresh();
    } catch (err) {
      setImportMsg(`Import failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setImporting(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  return (
    <>
      <ScreenHeader title={<>Finance Vault</>} subtitle={<>Money clarity in exact figures, kept private and local-first. Accounts, balances, and a calm
            monthly picture — no judgement.</>} actions={<><Chip tone="danger">vault</Chip></>} />

      <Pane title="Net worth" hint="Across all accounts">
        <div style={{ fontSize: '1.6rem', fontWeight: 600 }}>
          {netWorth.value !== undefined ? formatMinor(netWorth.value, currency) : '—'}
        </div>
        {s ? (
          <div style={{ display: 'flex', gap: 'var(--lc-stack-md)', marginTop: 8, flexWrap: 'wrap' }}>
            <Chip tone="primary">In {formatMinor(s.incomeMinor, currency)}</Chip>
            <Chip tone="warn">Out {formatMinor(s.expenseMinor, currency)}</Chip>
            <Chip tone={s.netMinor >= 0 ? 'primary' : 'danger'}>Net {formatMinor(s.netMinor, currency)}</Chip>
            <span style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)', alignSelf: 'center' }}>
              this month
            </span>
          </div>
        ) : null}
      </Pane>

      <Pane title="Accounts" hint={`${rows.length}`}>
        <Stack>
          <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              className="lc-cmdbar"
              style={{ flex: '1 1 180px' }}
              value={acctName}
              placeholder="Account name…"
              onChange={(e) => setAcctName(e.target.value)}
              aria-label="Account name"
            />
            <select value={acctType} onChange={(e) => setAcctType(e.target.value as AccountType)} aria-label="Account type" style={inputStyle}>
              {ACCOUNT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <input
              type="number"
              step="0.01"
              value={opening}
              onChange={(e) => setOpening(e.target.value)}
              aria-label="Opening balance"
              style={{ ...inputStyle, width: 120 }}
            />
            <Button variant="primary" onClick={() => void addAccount()}>
              Add account
            </Button>
          </div>

          {rows.length === 0 ? (
            <EmptyState title="No accounts yet">Add a cash, bank, or credit account to start tracking balances.</EmptyState>
          ) : (
            rows.map(({ account, balanceMinor }) => (
              <CompactRow
                key={String(account.id)}
                title={account.name}
                subtitle={account.accountType}
                meta={<strong>{formatMinor(balanceMinor, account.currency)}</strong>}
              />
            ))
          )}
        </Stack>
      </Pane>

      {rows.length > 0 ? (
        <Pane title="Import statement" hint="Bank/UPI history — deduplicated, never doubled">
          <Stack>
            <div style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)' }}>
              Load a normalized ledger (<code>canonical-ledger.json</code> from the offline importer).
              Google Pay is reconciled into the SBI ledger by UPI id, and re-imports are idempotent —
              rows already present are skipped, so nothing is ever duplicated.
            </div>
            <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
              <select value={importAccount || String(rows[0]!.account.id)} onChange={(e) => setImportAccount(e.target.value)} aria-label="Import into account" style={inputStyle}>
                {rows.map(({ account }) => (
                  <option key={String(account.id)} value={String(account.id)}>{account.name}</option>
                ))}
              </select>
              <input
                ref={fileInput}
                type="file"
                accept="application/json,.json"
                style={{ display: 'none' }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void runImport(f); }}
                aria-label="Ledger file"
              />
              <Button variant="primary" onClick={() => fileInput.current?.click()}>
                {importing ? 'Importing…' : 'Choose ledger file'}
              </Button>
            </div>
            {importMsg ? <Chip tone="primary">{importMsg}</Chip> : null}
            {(batches.value ?? []).length > 0 ? (
              <div>
                <div className="lc-label" style={{ marginBottom: 6 }}>Past imports</div>
                <Stack>
                  {[...(batches.value ?? [])]
                    .sort((a, b) => b.importedOn.localeCompare(a.importedOn))
                    .slice(0, 6)
                    .map((b) => (
                      <CompactRow
                        key={String(b.id)}
                        title={b.sourceLabel}
                        subtitle={`${b.importedOn.slice(0, 10)} · ${b.sourceFiles.join(', ')}`}
                        meta={<Chip tone="neutral">+{b.rowsCreated} · skip {b.rowsSkipped}</Chip>}
                      />
                    ))}
                </Stack>
              </div>
            ) : null}
          </Stack>
        </Pane>
      ) : null}

      {rows.length > 0 ? (
        <Pane title="Record a transaction">
          <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={txnAccount || String(rows[0]!.account.id)} onChange={(e) => setTxnAccount(e.target.value)} aria-label="Account" style={inputStyle}>
              {rows.map(({ account }) => (
                <option key={String(account.id)} value={String(account.id)}>
                  {account.name}
                </option>
              ))}
            </select>
            <select value={txnKind} onChange={(e) => setTxnKind(e.target.value as TransactionKind)} aria-label="Kind" style={inputStyle}>
              {TRANSACTION_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
            <input
              type="number"
              step="0.01"
              min="0"
              value={txnAmount}
              placeholder="0.00"
              onChange={(e) => setTxnAmount(e.target.value)}
              aria-label="Amount"
              style={{ ...inputStyle, width: 120 }}
            />
            <input
              className="lc-cmdbar"
              style={{ flex: '1 1 160px' }}
              value={txnCategory}
              placeholder="Category (e.g. groceries)"
              onChange={(e) => setTxnCategory(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void addTxn();
              }}
              aria-label="Category"
            />
            <Button variant="primary" onClick={() => void addTxn()}>
              Record
            </Button>
          </div>
        </Pane>
      ) : null}

      {byCategory.length > 0 ? (
        <Pane title="This month by category">
          <Stack>
            {byCategory.map(([cat, amt]) => (
              <CompactRow
                key={cat}
                title={cat}
                meta={<Chip tone={amt >= 0 ? 'primary' : 'warn'}>{formatMinor(amt, currency)}</Chip>}
              />
            ))}
          </Stack>
        </Pane>
      ) : null}

      <Pane title="Recent transactions" hint={`${recent.value?.length ?? 0}`}>
        <Stack>
          {(recent.value ?? []).length === 0 ? (
            <EmptyState title="No transactions yet">Record income or an expense above to see it here.</EmptyState>
          ) : (
            [...(recent.value ?? [])]
              .sort((a, b) => b.occurredOn.localeCompare(a.occurredOn))
              .slice(0, 20)
              .map((t) => (
                <CompactRow
                  key={String(t.id)}
                  title={t.counterparty || t.category || 'uncategorized'}
                  subtitle={`${t.rail ? `${t.rail} · ` : ''}${t.txnKind} · ${t.occurredOn.slice(0, 10)}`}
                  meta={
                    <Chip tone={t.txnKind === 'income' ? 'primary' : 'warn'}>
                      {t.txnKind === 'income' ? '+' : '-'}
                      {formatMinor(t.amountMinor, currency).replace(/^-/, '')}
                    </Chip>
                  }
                />
              ))
          )}
        </Stack>
      </Pane>
    </>
  );
}
